import { SwimlaneSummary } from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import { ProcessAutoLayoutResult } from '@flowx/designer/shared/entities/process-api.types'
import { SequencePoints } from '@flowx/designer/shared/entities/sequence.dictionary'

import { pointsEqual } from './drag-geometry.util'
import { isOrthogonalRoute } from './edges/orthogonal-edge.util'

import { CanvasData, NodeDefinitionRender } from '../../bpmn-canvas.types'
import { Point } from '../../dictionary/geometry.types'

export interface LaneLayout {
  x: number
  y: number
  width: number
  height: number
}

/** What an auto-layout changes, on the canvas's own nodes, lanes and sequences. */
export interface ProcessLayoutPlan {
  /** Lane-relative positions, the way nodes are persisted. */
  nodes: { node: NodeDefinitionRender; layoutOptions: Point }[]
  swimlanes: { swimlane: SwimlaneSummary; layoutOptions: LaneLayout }[]
  sequences: { sequence: SequencePoints; points: Point[] }[]
}

/** The backend's auto-layout as a plan over `source`; anything `source` no longer has is left out. */
export function toProcessLayoutPlan(
  source: CanvasData,
  result: ProcessAutoLayoutResult
): ProcessLayoutPlan {
  const nodesById = new Map(source.nodes.map((node) => [node.id, node]))
  const lanesById = new Map(source.swimlanes.map((swimlane) => [String(swimlane.id), swimlane]))
  const sequencesById = new Map(source.sequences.map((sequence) => [sequence.id, sequence]))

  return {
    nodes: result.nodes
      .filter(({ id }) => nodesById.has(id))
      .map(({ id, x, y }) => ({ node: nodesById.get(id), layoutOptions: { x, y } })),
    swimlanes: result.swimlanes
      .filter(({ id }) => lanesById.has(String(id)))
      .map(({ id, x, y, width, height }) => ({
        swimlane: lanesById.get(String(id)),
        layoutOptions: { x, y, width, height },
      })),
    sequences: result.sequences
      .filter(({ id }) => sequencesById.has(id))
      .map(({ id, points }) => ({ sequence: sequencesById.get(id), points })),
  }
}

/** Applies a plan's node and lane positions to canvas data (routes are derived from these). */
export function withLayout(
  data: CanvasData,
  plan: Pick<ProcessLayoutPlan, 'nodes' | 'swimlanes'>
): CanvasData {
  const nodePositions = new Map(
    plan.nodes.map(({ node, layoutOptions }) => [node.id, layoutOptions])
  )
  const lanePositions = new Map(
    plan.swimlanes.map(({ swimlane, layoutOptions }) => [swimlane.id, layoutOptions])
  )

  return {
    ...data,
    swimlanes: data.swimlanes.map((swimlane) =>
      lanePositions.has(swimlane.id)
        ? { ...swimlane, layoutOptions: lanePositions.get(swimlane.id) }
        : swimlane
    ),
    nodes: data.nodes.map((node) =>
      nodePositions.has(node.id) ? { ...node, layoutOptions: nodePositions.get(node.id) } : node
    ),
  }
}

const samePoint = (a: Point | null | undefined, b: Point): boolean => a?.x === b.x && a?.y === b.y

const sameLaneLayout = (a: Partial<LaneLayout> | null | undefined, b: LaneLayout): boolean =>
  a?.x === b.x && a?.y === b.y && a?.width === b.width && a?.height === b.height

/**
 * `data` as it would look once `plan` is saved: node and lane positions and sequence routes. The
 * canvas renders this read-only as the layout preview; nothing is persisted until Apply.
 */
export function applyLayoutPlan(data: CanvasData, plan: ProcessLayoutPlan): CanvasData {
  const arranged = withLayout(data, plan)
  const routes = new Map(plan.sequences.map(({ sequence, points }) => [sequence.id, points]))

  return {
    ...arranged,
    sequences: arranged.sequences.map((sequence) =>
      routes.has(sequence.id) ? { ...sequence, layoutOptions: routes.get(sequence.id) } : sequence
    ),
  }
}

/**
 * Whether `data` already shows everything `plan` changes. After Apply the preview stays up until
 * the live data catches up (lanes are saved first and only land once the API answers), so the
 * canvas never flashes the old layout in between.
 */
export function showsLayoutPlan(data: CanvasData, plan: ProcessLayoutPlan): boolean {
  const nodesById = new Map(data.nodes.map((node) => [node.id, node]))
  const lanesById = new Map(data.swimlanes.map((swimlane) => [swimlane.id, swimlane]))
  const sequencesById = new Map(data.sequences.map((sequence) => [sequence.id, sequence]))

  return (
    plan.nodes.every(({ node, layoutOptions }) =>
      samePoint(nodesById.get(node.id)?.layoutOptions, layoutOptions)
    ) &&
    plan.swimlanes.every(({ swimlane, layoutOptions }) =>
      sameLaneLayout(lanesById.get(swimlane.id)?.layoutOptions, layoutOptions)
    ) &&
    plan.sequences.every(({ sequence, points }) => {
      const current = sequencesById.get(sequence.id)?.layoutOptions

      return isOrthogonalRoute(current) && pointsEqual(current, points)
    })
  )
}
